import styled, { keyframes } from "styled-components";

/** Same wrapper pattern as GlobalCreateButton — relative shell, bare SVG trigger. */
export const StyledNotificationBell = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;

  > svg {
    display: block;
    /* Bell viewBox is 16×20; size so painted width matches the 18px plus. */
    width: 18px;
    height: 22px;
    cursor: pointer;
  }

  > .muteSlash {
    position: absolute;
    top: 50%;
    left: 9px;
    width: 22px;
    height: 1.5px;
    margin-left: -11px;
    background: #4a4b66;
    transform: translateY(-50%) rotate(-40deg);
    pointer-events: none;
    border-radius: 1px;
  }

  > .badge {
    position: absolute;
    top: -2px;
    left: 12px;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 16px;
    padding: 0 4px;
    border-radius: 999px;
    background: #e11d48;
    color: #fff;
    font-size: 9px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0;
    text-align: center;
    pointer-events: none;
  }
`;

/** Drops directly under the bell. Same offsets as GlobalCreateMenuDiv. */
export const StyledNotificationPanel = styled.div`
  position: absolute;
  z-index: 30;
  top: calc(100% + 8px);
  right: 0;
  width: 360px;
  max-width: min(360px, calc(100vw - 32px));
  border-radius: 4px;
  background: #fff;
  box-shadow:
    0 3px 6px -4px rgba(0, 0, 0, 0.12),
    0 6px 16px 0 rgba(0, 0, 0, 0.08);
  font-family: Inter, sans-serif;
  color: var(--Dark-Grey, #262641);
  cursor: default;
  overflow: hidden;

  /* Avoid ".header"/".footer": Metronic styles those globally as fixed page chrome. */
  > .ntfPanelHeader {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 16px 12px;
    border-bottom: 1px solid #e9edf5;

    > .titles {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0;

      > h3 {
        margin: 0;
        color: #0e0e2c;
        font-size: 16px;
        font-weight: 600;
        line-height: 120%;
      }

      > .subtitle {
        margin: 0;
        color: #667085;
        font-size: 12px;
        font-weight: 400;
        line-height: 150%;
      }
    }

    > .headerActions {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      flex-shrink: 0;
    }

    > .headerActions > .headerIconBtn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border: none;
      border-radius: 6px;
      background: transparent;
      color: #4a4b66;
      cursor: pointer;
      flex-shrink: 0;

      &:hover:not(:disabled) {
        background: var(--Mist, #f7f8f9);
        color: #1249bf;
      }

      &:disabled {
        opacity: 0.5;
        cursor: default;
      }
    }
  }

  > .ntfPanelList {
    max-height: min(480px, 65vh);
    overflow-y: auto;
    padding: 4px 0;
  }
`;

export const StyledNotificationRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  transition: background 0.15s ease;

  &:hover {
    background: var(--Mist, #f7f8f9);
  }

  > .unreadDot {
    width: 8px;
    height: 8px;
    margin-top: 6px;
    border-radius: 50%;
    background: ${props => (props.$unread ? "#2060ea" : "transparent")};
    flex-shrink: 0;
  }

  > .body {
    flex: 1;
    min-width: 0;
    cursor: ${props => (props.$clickable ? "pointer" : "default")};

    > .meta {
      color: #667085;
      font-size: 11px;
      font-weight: 500;
      margin-bottom: 4px;
    }

    > .content {
      color: ${props => (props.$unread ? "#0e0e2c" : "#4a4b66")};
      font-size: 13px;
      font-weight: ${props => (props.$unread ? 500 : 400)};
      line-height: 150%;
      word-break: break-word;
    }

    > .time {
      margin-top: 6px;
      color: #98a2b3;
      font-size: 11px;
      font-weight: 400;
    }
  }

  > .openLink {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    margin-top: 2px;
    border: none;
    border-radius: 6px;
    background: #eef3ff;
    color: #2060ea;
    cursor: pointer;

    &:hover {
      background: #dbe7ff;
    }
  }
`;

const pulse = keyframes`
  0% { opacity: 0.55; }
  50% { opacity: 1; }
  100% { opacity: 0.55; }
`;

export const StyledLoader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  animation: ${pulse} 1.2s ease-in-out infinite;

  > .row {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-bottom: 12px;
    border-bottom: 1px solid #f0f2f7;

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }

    > .line {
      height: 10px;
      border-radius: 4px;
      background: #e9edf5;

      &.short {
        width: 36%;
      }

      &.medium {
        width: 72%;
      }

      &.long {
        width: 92%;
      }
    }
  }
`;

export const StyledEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 28px 24px;
  text-align: center;

  > .iconWrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    margin-bottom: 2px;
    border-radius: 12px;
    background: #eef3ff;
    color: #2060ea;
    font-size: 20px;
  }

  > h4 {
    margin: 0;
    color: #0e0e2c;
    font-size: 14px;
    font-weight: 600;
    line-height: 140%;
  }

  > p {
    margin: 0;
    max-width: 240px;
    color: #667085;
    font-size: 12px;
    font-weight: 400;
    line-height: 150%;
  }
`;

export const StyledErrorState = styled(StyledEmptyState)`
  > .iconWrap {
    background: #fff1f0;
    color: #cf1322;
  }

  > .retryBtn {
    margin-top: 8px;
    border: none;
    border-radius: 8px;
    background: #2060ea;
    color: #fff;
    font-family: Inter, sans-serif;
    font-size: 12px;
    font-weight: 500;
    padding: 8px 14px;
    cursor: pointer;

    &:hover {
      background: #1249bf;
    }
  }
`;

export const StyledScrollHint = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px 8px;
  color: #98a2b3;
  font-size: 11px;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const StyledSpinner = styled.span`
  width: 14px;
  height: 14px;
  border: 2px solid #d0d5dd;
  border-top-color: #2060ea;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;
